import { DocumentCategory, EditingCategory } from 'constants/product'
import { EditingProductListing, ProductListing } from '../../../common'
import { GRAY_300, GRAY_600, WHITE } from 'constants/styling/theme'
import { isEditingCategory, isShootingCategory } from 'utils/validators'

import { Box, Typography } from '@mui/material'
import { EmbedCatalogueError } from 'components/pages/PurchaseFlow/_main/EmbedCatalogueError/EmbedCatalogueError'
import { EmbedPropertyError } from 'components/common/Embed/EmbedPropertyError/EmbedPropertyError'
import { EmptyProductList } from 'components/pages/PurchaseFlow/common/ProductListing/EmptyProductList'
import { EnergyProductListing } from 'components/pages/PurchaseFlow/common/EnergyProductListing/EnergyProductListing.component'
import { MUIButton } from 'components/common/MUIButton'
import { Nullable } from 'models/helpers'
import { ProductCategory } from 'models/product'
import { ProductFilter } from '../ProductFilters'
import { ProductSegment } from 'constants/product/productTypes'
import { ProductSegmentSlider } from '../ProductFilters/ProductSegmentSlider.component'
import { SquareFootOutlined } from '@mui/icons-material'
import { productTypesForHint } from 'components/common/Embed/embedCategoryHint'
import { PRODUCT_STEP_FILTER_SX, PRODUCT_STEP_LISTING_SX, PRODUCT_STEP_ROW_SX, supportsContainerQueries } from 'components/pages/PurchaseFlow/_main/purchaseFlowLayout'
import { segmentForSurface, sizeGateReason } from 'utils/purchaseFlow/segmentForSurface'
import { embedRepricedProps } from './embedRepricedProps'
import Stack from '@mui/material/Stack'
import { StepWrapper } from 'components/pages/PurchaseFlow/common/StepWrapper'
import TransitionAppear from 'components/common/TransitionAppear/TransitionAppear'
import { TransitionGroup } from 'react-transition-group'
import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { bigFromFee } from 'utils/price'
import { logger } from 'utils/logging'
import { useAuth0 } from 'utils/auth'
import { useBkbnSearchParams } from 'utils/hooks/useBkbnSearchParams'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useEmbedConfig } from 'components/pages/PurchaseFlow/_main/contexts/EmbedConfig.context'
import { usePurchaseFlowConfig, usePurchaseFlowRealEstateProperty } from 'components/pages/PurchaseFlow/_main/contexts'
import { selectionToPayload, usePurchaseFlowProducts } from 'components/pages/PurchaseFlow/_main/contexts/PurchaseFlowProducts.context'
import { useTargetOrderUser } from '../../../_main/contexts'
import { useTranslation } from 'react-i18next'

/** Auto-select normally settles in well under a second; anything past this is a stuck blank embed. */
const STUCK_AUTO_SEGMENT_WARN_MS = 10_000

/**
 * @component
 * ProductStepController represents the controller for the product selection step in the purchase flow.
 *
 * @example
 * <ProductStepController />
 */
export const ProductStepController: React.FC = () => {
  const { roles, user } = useAuth0()
  const { targetUser, adminSelectedUserEmail, setAdminSelectedUserEmail } = useTargetOrderUser()
  const { selectedSegment, selectedProducts, selectedFilterTypes, toggleFilterType, updateSelectedProduct, setSegment } = usePurchaseFlowProducts()
  const {
    sessionId,
    selectedCity,
    getProductCatalogue,
    selectedCategory,
    selectedCountryCode,
    getExtraProducts,
    regionCode,
    postalCode,
    allProducts,
    availableSegments,
    availableProductTypesPerSegment,
  } = usePurchaseFlowConfig()
  const { isEmbed, revenueChannel, isOneSystem, categoryHint } = useEmbedConfig()
  const { getPropertyQuery } = usePurchaseFlowRealEstateProperty()
  const params = useBkbnSearchParams()

  const { t } = useTranslation(['order', 'segment', 'segment_title', 'product_type'])

  // Embed defaults the admin target to the authed user (ClientStep is skipped), else the admin guard blocks the catalogue and dead-ends on an empty list
  useEffect(() => {
    if (!isEmbed) return
    if (!roles.isAdmin) return
    if (adminSelectedUserEmail) return
    if (!user?.email) return
    setAdminSelectedUserEmail(user.email)
  }, [isEmbed, roles.isAdmin, adminSelectedUserEmail, user?.email, setAdminSelectedUserEmail])

  useEffect(() => {
    logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_ENTERS_ORDER_PRODUCTS_STEP, {
      category: selectedCategory,
      countryCode: selectedCountryCode,
      city: selectedCity,
      revenueChannel,
      isOneSystem,
      // Width is the only axis the ISSUE-3168 layout defect lived on, and inside the Propstack
      // iframe it is the host page that decides it — without these two an embed rendering the
      // product column too narrow, or falling back to the un-queried layout, is indistinguishable
      // from a healthy one in every signal we collect.
      viewportWidth: window.innerWidth,
      supportsContainerQueries: supportsContainerQueries(),
    })
  }, [selectedCategory, selectedCountryCode, selectedCity, revenueChannel, isOneSystem])

  // Warn (empty catalogue otherwise) only once the property lookup has definitively failed; a missing city/country while in flight is just pending
  const embedUnresolvedLoggedRef = useRef(false)
  useEffect(() => {
    if (!isEmbed) return
    if (!selectedCategory) return
    if (!isShootingCategory(selectedCategory)) return
    if (selectedCity && selectedCountryCode) return
    if (!getPropertyQuery.isError) return
    if (embedUnresolvedLoggedRef.current) return

    embedUnresolvedLoggedRef.current = true
    logger.warn('Embed property could not be resolved for product pricing', undefined, {
      context: {
        component: 'ProductStep',
        action: 'embedPropertyUnresolved',
        metadata: { objectReferenceId: params.get('objectReferenceId') },
      },
    })
  }, [isEmbed, selectedCategory, selectedCity, selectedCountryCode, getPropertyQuery.isError, params])

  // The embed property-fetch failing dead-ends the broker on the full-screen EmbedPropertyError. Log the
  // underlying error once (with HTTP status) so the dead-end is triageable in production — a 404 (property
  // not synced), 403 (workspace/permission), 500 (backend) and a network failure look identical on screen.
  const embedPropertyErrorLoggedRef = useRef(false)
  useEffect(() => {
    if (!isEmbed) return
    if (!getPropertyQuery.isError) {
      embedPropertyErrorLoggedRef.current = false
      return
    }
    if (embedPropertyErrorLoggedRef.current) return

    embedPropertyErrorLoggedRef.current = true
    logger.warn('Embed property fetch failed; showing the property-error screen', getPropertyQuery.error, {
      context: {
        component: 'ProductStep',
        action: 'embedPropertyFetchFailed',
        metadata: {
          objectReferenceId: params.get('objectReferenceId'),
          status: getPropertyQuery.error?.response?.status ?? null,
        },
      },
    })
  }, [isEmbed, getPropertyQuery.isError, getPropertyQuery.error, params])

  /** Retrieves the configuration from the API  */
  useEffect(() => {
    if (!getProductCatalogue.isIdle) return
    if (!getExtraProducts.isIdle) return
    if (!selectedCategory || !sessionId) return
    if (roles.isAdmin && !adminSelectedUserEmail) return
    if (isShootingCategory(selectedCategory) && (!selectedCity || !selectedCountryCode)) return

    getProductCatalogue.mutate({
      sessionId,
      email: targetUser.email,
      category: selectedCategory,
      countryCode: selectedCountryCode || targetUser.country,
      city: selectedCity,
      regionCode,
      postalCode,
      revenueChannel,
      ...(isEmbed && {
        realEstatePropertyId: getPropertyQuery.data?.id ?? null,
        selectedProducts: selectionToPayload(selectedProducts),
      }),
    })

    getExtraProducts.mutate({
      sessionId,
      email: targetUser.email,
      category: selectedCategory,
      countryCode: new Set<ProductCategory>([EditingCategory.EDITING, EditingCategory.STAGING]).has(selectedCategory) || !selectedCountryCode
        ? targetUser.country
        : selectedCountryCode,
      regionCode,
      postalCode,
    })

  }, [selectedCategory, sessionId, adminSelectedUserEmail, roles, targetUser, getProductCatalogue, selectedCity, selectedCountryCode, getExtraProducts, regionCode, postalCode, revenueChannel, isOneSystem, isEmbed, getPropertyQuery.data?.id, selectedProducts])

  // Live OneSystem repricing (embed only). The signature is IDs+quantities only, so a price-only
  // update from the sync effect below never retriggers a refetch (guards against a render loop).
  const selectionSignature = useMemo(
    () => selectionToPayload(selectedProducts).map(({ productId, quantity }) => `${productId}:${quantity}`).join(','),
    [selectedProducts]
  )
  const propertyId = getPropertyQuery.data?.id ?? null
  const lastRepricedSignature = useRef(selectionSignature)

  // Stale-response guard: rapid successive selections can put several reprice mutations in flight.
  // Each fire captures the signature it was fired for; the sync effect below only applies a catalogue
  // whose signature is still the latest fired one, so an older resolution can never overwrite a newer
  // selection's prices. (react-query v5 keeps only the latest mutate's `data`, but we do not rely on
  // that undocumented nuance — the explicit signature guard holds regardless.)
  const latestFiredSignature = useRef(selectionSignature)

  useEffect(() => {
    if (!isEmbed || !sessionId || !selectedCategory) return
    if (selectionSignature === lastRepricedSignature.current) return

    lastRepricedSignature.current = selectionSignature
    latestFiredSignature.current = selectionSignature

    getProductCatalogue.mutate({
      sessionId,
      email: targetUser.email,
      category: selectedCategory,
      countryCode: selectedCountryCode || targetUser.country,
      city: selectedCity,
      regionCode,
      postalCode,
      revenueChannel,
      realEstatePropertyId: propertyId,
      selectedProducts: selectionToPayload(selectedProducts),
    }, {
      // react-query runs a mutate-level callback only for the latest call ("Consecutive mutations"), so a superseded
      // reprice never reports and a failed one sends nothing. It runs inside the mutation: a throw would turn the
      // successful reprice into a failed one and keep the old prices on screen, hence the catch.
      onSuccess: (response) => {
        try {
          logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_EMBED_REPRICED, embedRepricedProps(selectedProducts, response.data.data))
        } catch (error) {
          logger.error('Embed reprice event could not be computed', error, {
            context: { component: 'ProductStep', action: 'embedRepricedEventFailed' },
          })
        }
      },
    })

    // Only the signature drives the reprice; the rest are stable payload inputs read at fire time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmbed, selectionSignature])

  // On a fresh catalogue, pull each selected product's server-computed feePrice/originalPrice back
  // into the selection so covered/charged rendering follows the live reprice. Only dispatch when a
  // value actually changed, so this never perpetuates a render loop (embed only).
  useEffect(() => {
    if (!isEmbed) return
    // Stale-response guard: skip while a newer reprice is still in flight, and skip a catalogue that was
    // fired for a signature other than the current selection (an older resolution landing after a newer fire).
    if (getProductCatalogue.isPending) return
    if (latestFiredSignature.current !== selectionSignature) {
      // Signal the race path so a "prices don't match my cart" report is diagnosable in production.
      logger.info('Embed reprice response dropped as stale (newer selection supersedes it)', undefined, {
        context: {
          component: 'ProductStep',
          action: 'embedRepriceStaleDrop',
          metadata: { firedFor: latestFiredSignature.current, currentSelection: selectionSignature },
        },
      })
      return
    }

    for (const catalogueProduct of allProducts) {
      const selected = selectedProducts[catalogueProduct.id]
      if (!selected) continue

      const feeChanged = !bigFromFee(selected.feePrice).eq(bigFromFee(catalogueProduct.feePrice))
      const originalChanged =
        (selected.originalPrice == null) !== (catalogueProduct.originalPrice == null) ||
        (!!selected.originalPrice && !!catalogueProduct.originalPrice &&
          !bigFromFee(selected.originalPrice).eq(bigFromFee(catalogueProduct.originalPrice)))

      if (!feeChanged && !originalChanged) continue

      updateSelectedProduct(catalogueProduct.id, {
        feePrice: catalogueProduct.feePrice,
        originalPrice: catalogueProduct.originalPrice,
      })
    }
    // selectedProducts is intentionally excluded: this effect reacts to fresh catalogue data, and
    // its own updateSelectedProduct dispatch would otherwise re-run it (loop). The changed-value
    // guard above makes a stale-selection read at worst a no-op on the next catalogue change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmbed, allProducts, updateSelectedProduct, getProductCatalogue.isPending, selectionSignature])

  // Whether any catalogue reached the step with something to offer: the live mutation result, or the embed
  // snapshot kept across reprices (the config context surfaces both as products/segments).
  const catalogueLoaded = allProducts.length > 0 || availableSegments.length > 0

  // The catalogue settled with nothing to show (ISSUE-3396): a failed load, or a 200 with no products for this
  // account. Both are the same dead-end (with no bands the gate would render an empty slider, or the step a blank
  // pending state), so both get the explicit error screen. In flight, or never fired, is not settled.
  const catalogueSettled = (getProductCatalogue.isError || getProductCatalogue.isSuccess) && !getProductCatalogue.isPending
  const showCatalogueError = isEmbed && catalogueSettled && !catalogueLoaded
  const catalogueErrorStatus = getProductCatalogue.error?.response?.status ?? null

  // Log the dead-end once with the HTTP status so it is triageable: a 404 (no pricebook for the account's country),
  // a 500, a network failure and an empty 200 look identical on screen. Status only, never the raw axios error:
  // the logger ships its data argument to Sentry (bearer, request body) and drops 401s entirely (ISSUE-2094).
  const catalogueErrorLoggedRef = useRef(false)
  useEffect(() => {
    if (!showCatalogueError) {
      catalogueErrorLoggedRef.current = false
      return
    }
    if (catalogueErrorLoggedRef.current) return

    catalogueErrorLoggedRef.current = true
    logger.warn('Embed catalogue settled with nothing to offer; showing the catalogue-error screen', undefined, {
      context: {
        component: 'ProductStep',
        action: 'embedCatalogueLoadFailed',
        metadata: {
          objectReferenceId: params.get('objectReferenceId'),
          reason: getProductCatalogue.isError ? 'error' : 'empty',
          status: catalogueErrorStatus,
        },
      },
    })
  }, [showCatalogueError, getProductCatalogue.isError, catalogueErrorStatus, params])

  // Resetting both mutations puts them back to idle, which is exactly what the load effect above waits for: the
  // retry re-fires the same request without duplicating its parameters here. Covers the transient failures (502,
  // dropped connection) that would otherwise be as terminal as a permanent 404 inside the iframe.
  const retryCatalogue = () => {
    getProductCatalogue.reset()
    getExtraProducts.reset()
  }

  // Refetch failure is non-blocking: keep the last successful catalogue (react-query leaves mutation
  // `data` untouched on error) and only warn. The user can still order; placement re-prices server-side.
  // A first load that failed is not a reprice: the catalogue-error effect above reports it instead.
  const repriceErrorLoggedRef = useRef(false)
  useEffect(() => {
    if (!isEmbed) return
    if (!getProductCatalogue.isError) {
      repriceErrorLoggedRef.current = false
      return
    }
    if (!catalogueLoaded) return
    if (repriceErrorLoggedRef.current) return

    repriceErrorLoggedRef.current = true
    logger.warn('Embed live repricing refetch failed; keeping last catalogue', undefined, {
      context: {
        component: 'ProductStep',
        action: 'embedRepriceFailed',
        metadata: {
          objectReferenceId: params.get('objectReferenceId'),
          status: catalogueErrorStatus,
        },
      },
    })
  }, [isEmbed, getProductCatalogue.isError, catalogueLoaded, catalogueErrorStatus, params])

  // Auto-select the area segment from the prefilled property's surface (embed only): when no segment
  // is chosen yet and the surface maps to a band that the catalogue actually offers, set it once so the
  // slider can be hidden. If the surface is missing or the mapped band is unavailable, we never guess —
  // the blocking gate below asks the user to pick the area manually.
  const autoSegmentRef = useRef(false)
  const autoSegmentFallbackLoggedRef = useRef(false)
  useEffect(() => {
    if (!isEmbed) return
    if (selectedSegment) return
    if (autoSegmentRef.current) return

    const band = segmentForSurface(getPropertyQuery.data?.surface)
    const bandOffered = !!band && availableSegments.includes(band)
    // A one-band catalogue leaves the gate nothing to ask. Waits for the property, whose surface may map to that band.
    if (!bandOffered && getPropertyQuery.isSuccess && availableSegments.length === 1) {
      autoSegmentRef.current = true
      setSegment(availableSegments[0], 0, 'single_band')
      return
    }

    if (!band) {
      // Only meaningful once the property actually resolved (a not-yet-loaded property is not a fallback).
      if (getPropertyQuery.isSuccess && !autoSegmentFallbackLoggedRef.current) {
        autoSegmentFallbackLoggedRef.current = true
        logger.warn('Embed area auto-select fell back to the manual gate', undefined, {
          context: {
            component: 'ProductStep',
            action: 'embedAutoSegmentFallback',
            metadata: { reason: sizeGateReason(getPropertyQuery.data?.surface), surface: getPropertyQuery.data?.surface ?? null },
          },
        })
      }
      return
    }

    const bandIndex = availableSegments.indexOf(band)
    if (bandIndex < 0) {
      // Only meaningful once the catalogue delivered its bands (an empty list is just a pending load).
      if (availableSegments.length > 0 && !autoSegmentFallbackLoggedRef.current) {
        autoSegmentFallbackLoggedRef.current = true
        logger.warn('Embed area auto-select fell back to the manual gate', undefined, {
          context: {
            component: 'ProductStep',
            action: 'embedAutoSegmentFallback',
            metadata: { reason: 'band_unavailable', band },
          },
        })
      }
      return
    }

    autoSegmentRef.current = true
    setSegment(band, bandIndex, 'property_surface')
  }, [isEmbed, selectedSegment, availableSegments, getPropertyQuery.data?.surface, getPropertyQuery.isSuccess, setSegment])

  // Apply the value-proposition overlay's service hint once (embed only, ISSUE-2017): pre-select
  // the mapped product-type filters as soon as a segment is chosen and the catalogue's availability
  // for it is known. The hint is intersected with availability, so an unavailable hinted service
  // degrades to the normal unfiltered step instead of an empty product list. Ref-guarded so a later
  // manual un-toggle by the user is never fought by this effect.
  const categoryHintAppliedRef = useRef(false)
  useEffect(() => {
    if (!isEmbed || !categoryHint) return
    if (categoryHintAppliedRef.current) return
    if (!selectedSegment) return

    const availableTypes = (availableProductTypesPerSegment[selectedSegment]?.all ?? [])
      .map(({ productType }) => productType)
    if (availableTypes.length === 0) return

    categoryHintAppliedRef.current = true
    for (const type of productTypesForHint(categoryHint, availableTypes)) {
      if (!selectedFilterTypes.has(type)) toggleFilterType(type)
    }
  }, [isEmbed, categoryHint, selectedSegment, availableProductTypesPerSegment, selectedFilterTypes, toggleFilterType])

  // The band the auto-select effect above would pick (null while the property is unresolved/unmappable).
  const autoSegmentBand = useMemo(() => segmentForSurface(getPropertyQuery.data?.surface), [getPropertyQuery.data?.surface])

  // The auto-select may still fire: the property is still resolving, its band is known and the catalogue
  // has not delivered its segments yet or does offer the band, or the catalogue offers a single band (the
  // effect will commit it). Holding the gate during that window keeps the slider from flashing.
  const isAutoSegmentPending = isEmbed && !selectedSegment && !autoSegmentRef.current && !showCatalogueError && (
    (!getPropertyQuery.isSuccess && !getPropertyQuery.isError) ||
    (autoSegmentBand !== null && (availableSegments.length === 0 || availableSegments.indexOf(autoSegmentBand) >= 0)) ||
    (getPropertyQuery.isSuccess && availableSegments.length === 1)
  )

  // While pending the step renders null and the footer is hidden too, so a pending state that never
  // settles (property query never enabled, catalogue never delivering segments) is a fully blank
  // embed with zero log lines — one-shot warn so it is diagnosable in production.
  const stuckPendingLoggedRef = useRef(false)
  useEffect(() => {
    if (!isAutoSegmentPending || stuckPendingLoggedRef.current) return

    const timer = window.setTimeout(() => {
      stuckPendingLoggedRef.current = true
      logger.warn('Embed area auto-select still pending — the embed is rendering nothing', undefined, {
        context: {
          component: 'ProductStep',
          action: 'embedAutoSegmentStuck',
          metadata: {
            propertyResolved: getPropertyQuery.isSuccess,
            hasMappableBand: autoSegmentBand !== null,
            availableSegmentCount: availableSegments.length,
            // Separates "catalogue still in flight" from "catalogue never fired" (city/country never resolved).
            catalogueStatus: getProductCatalogue.status,
          },
        },
      })
    }, STUCK_AUTO_SEGMENT_WARN_MS)

    return () => window.clearTimeout(timer)
  }, [isAutoSegmentPending, getPropertyQuery.isSuccess, autoSegmentBand, availableSegments.length, getProductCatalogue.status])

  // Embed with no segment yet: block the product listing behind a gate that asks for the property area
  // first (surface unknown or not offered by the catalogue). The gate hosts the only segment slider, so
  // once a segment is picked the slider disappears (single source of truth).
  const showSegmentGate = isEmbed && !selectedSegment && !isAutoSegmentPending

  // When the prefilled embed property never resolved, the whole step cannot be priced: show an explicit
  // error screen (mirrors the session-error UX) instead of an empty catalogue or the segment gate. This
  // takes precedence over the segment gate — there is nothing meaningful to select without a property.
  const showPropertyError = isEmbed && getPropertyQuery.isError

  // The gate is a funnel step of its own (ISSUE-4022): embed_size_gate_blocked once per flow when it renders, with
  // why the auto-select gave up. Keyed on the rendered state, so the error screens that replace the gate never
  // count; a surface-less gate counts only once the catalogue delivered its bands (before that the broker cannot
  // pick, and a catalogue that then fails ends on the error screen, not on the gate); ref-guarded like the
  // fallback warning above so a rerender never re-sends it.
  const isSegmentGateRendered = showSegmentGate && !showPropertyError && !showCatalogueError && availableSegments.length > 0
  const gateBlockedLoggedRef = useRef(false)
  useEffect(() => {
    if (!isSegmentGateRendered || gateBlockedLoggedRef.current) return

    gateBlockedLoggedRef.current = true
    logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_EMBED_SIZE_GATE_BLOCKED, { reason: sizeGateReason(getPropertyQuery.data?.surface) })
  }, [isSegmentGateRendered, getPropertyQuery.data?.surface])

  // Gate and property-error are full-screen embed states with their own centered layout: render them
  // chrome-less (no StepWrapper title/subtitle, no product filters) so the embed matches the prototype.
  if (showPropertyError) {
    return (
      <TransitionAppear visible>
        <EmbedPropertyError />
      </TransitionAppear>
    )
  }

  if (showCatalogueError) {
    return (
      <TransitionAppear visible>
        <EmbedCatalogueError onRetry={retryCatalogue} />
      </TransitionAppear>
    )
  }

  // Auto-select still in flight: render nothing rather than flashing the manual slider (resolves <1s).
  if (isAutoSegmentPending) {
    return null
  }

  if (showSegmentGate) {
    return (
      <TransitionAppear visible>
        <SegmentGate onCommit={(segment, index) => setSegment(segment, index, 'size_gate')} />
      </TransitionAppear>
    )
  }

  return (
    <TransitionAppear visible>
      <StepWrapper
        title={t('step_product.title')}
        subtitle={t('step_product.text', { segment: t(`segment_title:${selectedCategory}`).toLocaleLowerCase() })}
      >
        <Stack
          width="100%"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          sx={PRODUCT_STEP_ROW_SX}
        >

          {/** LEFT CONTENT */}
          {selectedCategory !== DocumentCategory.ENERGY_CERTIFICATE &&
            <Box sx={PRODUCT_STEP_FILTER_SX}>
              <ProductFilter hideSegmentSlider={isEmbed} />
            </Box>
          }

          {/** MAIN CONTENT */}
          <Stack sx={PRODUCT_STEP_LISTING_SX}>
            <TransitionGroup component={null}>

              {getProductCatalogue.isSuccess &&
                <EmptyProductList />
              }

              {!!selectedCategory && isEditingCategory(selectedCategory)
                ? (
                  <EditingProductListing
                    disabled={!selectedSegment}
                  />
                )
                : (
                  selectedCategory === DocumentCategory.ENERGY_CERTIFICATE
                    ? <EnergyProductListing />
                    : <ProductListing disabled={!selectedSegment} />
                )
              }

            </TransitionGroup>
          </Stack>

        </Stack>
      </StepWrapper>
    </TransitionAppear>
  )
}

/** Props for the SegmentGate component. */
interface SegmentGateProps {
  /** Commits the chosen area band once the user confirms with the button. */
  onCommit: (segment: ProductSegment, index: number) => void
}

/**
 * Centered blocking gate shown in the embed when no area segment is selected: a ruler icon, a prompt
 * to pick the property area first, the area slider, and a "Weiter" button that commits the pending
 * band. The slider only stages a pending selection (no auto-validate); the button (disabled until a
 * band is picked) is the single commit, matching the Propstack prototype. Product cards stay hidden
 * until a segment is committed.
 */
const SegmentGate: React.FC<SegmentGateProps> = ({ onCommit }) => {
  const { t } = useTranslation(['embed', 'common'])

  const [pending, setPending] = useState<Nullable<{ segment: ProductSegment, index: number }>>(null)

  return (
    <Stack spacing={3} alignItems="center" justifyContent="center" sx={{ py: 6, textAlign: 'center' }}>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: WHITE,
          border: `1px solid ${GRAY_300}`,
          boxShadow: '0px 2px 8px 0px rgba(19, 24, 29, 0.08)',
        }}
      >
        <SquareFootOutlined sx={{ fontSize: 32, color: GRAY_600 }} />
      </Stack>
      <Typography variant="h6">{t('segment_gate.title')}</Typography>
      <Box width="100%" maxWidth={480}>
        <ProductSegmentSlider
          onSelect={(segment, index) => setPending({ segment, index })}
          pendingSegment={pending?.segment ?? null}
        />
      </Box>
      <MUIButton
        disabled={!pending}
        onClick={() => { if (pending) onCommit(pending.segment, pending.index) }}
      >
        {t('common:Next')}
      </MUIButton>
    </Stack>
  )
}
